import { useState } from 'react';
import { BRACKET_SIZES, type LobbySettings as Settings } from '@srcbattle/shared';
import { api, ApiRequestError, type LobbyView } from '../api.ts';
import { Banner, Card } from './ui.tsx';
import { SettingsPresets } from './SettingsPresets.tsx';

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="text-xs text-text-faint">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  disabled,
  name,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  disabled: boolean;
  /** Stable hook for tests and for scoping styles; never shown to anyone. */
  name: string;
}) {
  return (
    <div data-setting={name} className="flex gap-1 rounded-lg border border-border p-1">
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={`rounded px-2.5 py-1 text-xs transition-colors disabled:opacity-50 ${
            value === option.value
              ? 'bg-accent text-accent-fg'
              : 'text-text-muted hover:text-text'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Category({
  title,
  hint,
  defaultOpen = false,
  children,
}: {
  title: string;
  hint?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 py-3 text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs font-semibold uppercase tracking-wider text-accent">{title}</span>
          {hint && <span className="block text-xs text-text-faint">{hint}</span>}
        </span>
        <span aria-hidden className="shrink-0 text-xs text-text-faint">
          {open ? '−' : '+'}
        </span>
      </button>
      {open && <div className="pb-3">{children}</div>}
    </section>
  );
}

function SettingSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-2 first:mt-0">
      <h4 className="border-b border-border py-2 text-xs font-medium text-text-muted">{title}</h4>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

/**
 * The controls themselves, with no opinion about where they are saved.
 *
 * Shared by the lobby panel and the admin page's defaults, so the two can never
 * drift apart — a setting added in one place appears in the other by
 * construction rather than by somebody remembering.
 */
export function SettingsControls({
  settings,
  locked,
  patch,
}: {
  settings: Settings;
  locked: boolean;
  patch: (update: Partial<Settings>) => void;
}) {
  const yesNo = (value: boolean, key: keyof Settings, yes = 'On', no = 'Off') => (
    <Segmented
      value={value ? 'yes' : 'no'}
      disabled={locked}
      name={String(key)}
      onChange={(next) => patch({ [key]: next === 'yes' })}
      options={[{ value: 'no', label: no }, { value: 'yes', label: yes }]}
    />
  );

  return (
    <div>
      <Category title="General settings" defaultOpen>
        <SettingSection title="Battle setup">
          <Row label="Bracket size">
            <Segmented value={settings.bracketSize} disabled={locked} name="bracketSize"
              onChange={(bracketSize) => patch({ bracketSize })}
              options={BRACKET_SIZES.map((size) => ({ value: size, label: String(size) }))} />
          </Row>
          <Row label="Clip length" hint="How much of each entry plays.">
            <Segmented value={settings.clipLengthMs} disabled={locked} name="clipLengthMs"
              onChange={(clipLengthMs) => patch({ clipLengthMs })}
              options={[{ value: 30_000, label: '30s' }, { value: 60_000, label: '60s' }, { value: 90_000, label: '90s' }, { value: 120_000, label: '2m' }]} />
          </Row>
          <Row label="Ties" hint="What happens on an even split.">
            <Segmented value={settings.tieRule} disabled={locked} name="tieRule"
              onChange={(tieRule) => patch({ tieRule })}
              options={[{ value: 'tiebreak_round', label: 'Replay' }, { value: 'coin_toss', label: 'Coin toss' }, { value: 'operator_decides', label: 'Host picks' }]} />
          </Row>
        </SettingSection>

        <SettingSection title="Game layout">
          <Row label="Layout">
            <Segmented value={settings.gameLayout} disabled={locked} name="gameLayout"
              onChange={(gameLayout) => patch({ gameLayout })}
              options={[{ value: 'legacy', label: 'Legacy' }, { value: 'arena', label: 'Arena' }, { value: 'spotlight', label: 'Spotlight' }, { value: 'mixtape', label: 'Mixtape' }]} />
          </Row>
        </SettingSection>

        <SettingSection title="Game modes">
          <Row label="Wildcard revival" hint="After round one, the best-supported loser challenges the weakest winner for their place.">
            {yesNo(settings.wildcardRevival, 'wildcardRevival')}
          </Row>
          <Row label="Guess the submitter" hint="Round one only. The best guesser is shown at the end.">
            {yesNo(settings.guessingMode, 'guessingMode')}
          </Row>
        </SettingSection>

        <SettingSection title="Theme">
          <div className="py-2.5">
            <label htmlFor="theme-prompt" className="text-sm font-medium">Theme prompt</label>
            <p className="text-xs text-text-faint">Shown when people submit.</p>
            <input id="theme-prompt" defaultValue={settings.themePrompt ?? ''} disabled={locked} maxLength={120}
              placeholder="pre-2005 only"
              onBlur={(event) => {
                const next = event.target.value.trim();
                if (next !== (settings.themePrompt ?? '')) patch({ themePrompt: next || null });
              }}
              className="mt-2 w-full rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm disabled:opacity-50" />
          </div>
        </SettingSection>
      </Category>

      <Category title="Advanced settings">
        <SettingSection title="Entries and draw">
          <Row label="Seeding" hint="How entries are drawn into the bracket.">
            <Segmented value={settings.seedingMode} disabled={locked} name="seedingMode"
              onChange={(seedingMode) => patch({ seedingMode })}
              options={[{ value: 'no_self_clash', label: 'Spread out' }, { value: 'pure_random', label: 'Random' }, { value: 'reshuffle_each_round', label: 'Reshuffle' }]} />
          </Row>
          <Row label="Clip start">
            <Segmented value={settings.clipMode} disabled={locked} name="clipMode"
              onChange={(clipMode) => patch({ clipMode })}
              options={[{ value: 'submitter_choice', label: 'Submitter picks' }, { value: 'smart', label: 'Smart-ish' }, { value: 'fixed', label: 'From the top' }]} />
          </Row>
          <Row label="Clip timer" hint="Off lets the host decide when each song stops.">
            {yesNo(settings.clipLengthEnabled, 'clipLengthEnabled')}
          </Row>
        </SettingSection>

        <SettingSection title="Voting and reveals">
          <Row label="Voting opens">
            <Segmented value={settings.votingOpens} disabled={locked} name="votingOpens"
              onChange={(votingOpens) => patch({ votingOpens })}
              options={[{ value: 'after_both', label: 'After both' }, { value: 'anytime', label: 'Anytime' }]} />
          </Row>
          <Row label="Vote for your own">
            {yesNo(settings.allowSelfVote, 'allowSelfVote', 'Allowed', 'Blocked')}
          </Row>
          <Row label="Spectators vote">
            {yesNo(settings.spectatorsVote, 'spectatorsVote', 'Yes', 'No')}
          </Row>
          <Row label="Show the bracket" hint="Show upcoming matchups before the final.">
            {yesNo(settings.showBracket, 'showBracket', 'Shown', 'Hidden')}
          </Row>
          <Row label="Hide submitters" hint="Reveal submitters at the end instead of after each match.">
            {yesNo(settings.anonymousUntilEnd, 'anonymousUntilEnd', 'Until the end', 'Per match')}
          </Row>
        </SettingSection>

        <SettingSection title="Room reactions">
          <Row label="Emoji reactions">{yesNo(settings.reactionsEnabled, 'reactionsEnabled')}</Row>
          <Row label="Messages">
            <Segmented value={settings.chatMode} disabled={locked} name="chatMode" onChange={(chatMode) => patch({ chatMode })}
              options={[{ value: 'emoji_only', label: 'Emoji only' }, { value: 'chat', label: 'Emoji + chat' }]} />
          </Row>
          <Row label="Spam delay" hint="Time between each person's reactions.">
            <Segmented value={settings.reactionCooldownMs} disabled={locked} name="reactionCooldownMs"
              onChange={(reactionCooldownMs) => patch({ reactionCooldownMs })}
              options={[{ value: 1_000, label: '1s' }, { value: 2_000, label: '2s' }, { value: 5_000, label: '5s' }, { value: 10_000, label: '10s' }]} />
          </Row>
          <Row label="Super messages" hint="Image or GIF reactions.">{yesNo(settings.superReactionsEnabled, 'superReactionsEnabled')}</Row>
          {settings.superReactionsEnabled && <Row label="Super messages each" hint="Maximum per player, per game.">
            <Segmented value={settings.superReactionsPerPlayer} disabled={locked} name="superReactionsPerPlayer"
              onChange={(superReactionsPerPlayer) => patch({ superReactionsPerPlayer })}
              options={[1, 3, 5, 10].map((value) => ({ value, label: String(value) }))} />
          </Row>}
        </SettingSection>
      </Category>

      <Category title="Experimental settings" hint="May not work in every browser.">
        <SettingSection title="Playback source">
          <Row label="Audio" hint="Host only plays in the host's browser. Synced plays in everyone's.">
            <Segmented value={settings.audioMode} disabled={locked} name="audioMode" onChange={(audioMode) => patch({ audioMode })}
              options={[{ value: 'host_only', label: 'Host only' }, { value: 'synced', label: 'Synced' }]} />
          </Row>
          <Row label="Spotify links">
            <Segmented value={settings.spotifyLinks} disabled={locked} name="spotifyLinks" onChange={(spotifyLinks) => patch({ spotifyLinks })}
              options={[{ value: 'play_on_spotify', label: 'Play from Spotify' }, { value: 'find_on_youtube', label: 'Find on YouTube' }]} />
          </Row>
        </SettingSection>
      </Category>
    </div>
  );
}

export function LobbySettingsPanel({
  lobby,
  isHost,
  onSettingsApplied,
}: {
  lobby: LobbyView;
  isHost: boolean;
  /** Called after a preset rewrites the whole settings object at once. */
  onSettingsApplied?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const settings = lobby.settings as Settings;

  async function patch(update: Partial<Settings>) {
    if (!isHost) return;
    setBusy(true);
    setError(null);
    try {
      await api.updateSettings(lobby.id, update);
      // The socket broadcast carries the new state back to everyone, this
      // client included, so there is nothing to set locally.
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="mb-1 font-medium">Settings</h2>
      {!isHost && (
        <p className="mb-2 text-xs text-text-faint">Only the host can change these.</p>
      )}

      {error && (
        <div className="my-2">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      <SettingsControls
        settings={settings}
        locked={!isHost || busy}
        patch={(update) => void patch(update)}
      />

      {/* Below the controls, not above: a preset is a shortcut for the thing
          you have just been looking at, and putting it first would ask people
          to choose a saved setup before they know what is in one. */}
      <SettingsPresets
        lobbyId={lobby.id}
        settings={settings}
        isHost={isHost}
        onApplied={() => onSettingsApplied?.()}
      />
    </Card>
  );
}
